/**
 * Inhaltsformat von Kommentaren (versioniert):
 *  - 1 = reiner Text (alte Kommentare, Importe); Erwähnungen als „@Name“ im Text
 *  - 2 = HTML aus dem Kommentar-Editor (Tiptap) mit fester Auswahl an Elementen;
 *        Erwähnungen als <span data-mention="<Personen-ID>">@Name</span>
 * Bilder und Dateien stehen nicht im HTML, sondern als Anhänge am Kommentar.
 *
 * Die Funktionen hier sind reine Zeichenkettenfunktionen (Server und Browser, ohne DOM).
 */

export const COMMENT_FORMAT_TEXT = 1 as const;
export const COMMENT_FORMAT_RICH = 2 as const;
export type CommentFormat = typeof COMMENT_FORMAT_TEXT | typeof COMMENT_FORMAT_RICH;
export const MAX_COMMENT_ATTACHMENTS = 20;

const ALLOWED = new Set(['p', 'br', 'strong', 'em', 's', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'a', 'span']);
const VOID = new Set(['br']);
const ALIAS: Record<string, string> = { b: 'strong', i: 'em', strike: 's', del: 's' };
const DROP_WITH_CONTENT = /<(script|style|iframe|object|embed|template|svg|math|textarea|title|noscript|select|head)\b[\s\S]*?<\/\1\s*>/gi;
const MENTION_ID = /^[A-Za-z0-9_-]{1,64}$/;

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function decodeEntities(s: string): string {
  return s.replace(/&(#\d+|#x[0-9a-f]+|amp|lt|gt|quot|apos|nbsp|#39);/gi, (_m, e: string) => {
    const k = e.toLowerCase();
    if (k === 'amp') return '&';
    if (k === 'lt') return '<';
    if (k === 'gt') return '>';
    if (k === 'quot') return '"';
    if (k === 'apos' || k === '#39') return "'";
    if (k === 'nbsp') return ' ';
    const code = k.startsWith('#x') ? parseInt(k.slice(2), 16) : parseInt(k.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
  });
}

function parseAttrs(src: string): Map<string, string> {
  const out = new Map<string, string>();
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src))) out.set(m[1]!.toLowerCase(), decodeEntities(m[2] ?? m[3] ?? m[4] ?? ''));
  return out;
}

/** Nur http(s) und mailto; Steuerzeichen und Leerraum im Schema zählen nicht */
export function safeHref(raw: string): string | null {
  const v = raw.trim();
  const probe = v.replace(/[\u0000- \u007f-\u009f]/g, '').toLowerCase();
  if (!/^(https?:|mailto:)/.test(probe)) return null;
  return v.length <= 2000 ? v : null;
}

/**
 * Lässt nur die Elemente des Kommentar-Editors durch (Positivliste), schließt offene Elemente,
 * entfernt alle übrigen Attribute. Unbekannte Elemente verschwinden, ihr Text bleibt.
 */
export function sanitizeCommentHtml(input: string): string {
  const src = input.replace(/<!--[\s\S]*?-->/g, '').replace(DROP_WITH_CONTENT, '');
  const out: string[] = [];
  /** Offene Elemente; „!name“ = verworfenes Element (sein Schlusstag wird ignoriert) */
  const stack: string[] = [];
  const close = (entry: string) => {
    if (!entry.startsWith('!')) out.push(`</${entry}>`);
  };
  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>|<|>|[^<>]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src))) {
    const tok = m[0];
    if (m[2] === undefined) {
      // Text: einzelne spitze Klammern und nackte & maskieren, Entitäten unverändert lassen
      if (tok === '<') out.push('&lt;');
      else if (tok === '>') out.push('&gt;');
      else out.push(tok.replace(/&(?!(#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);)/g, '&amp;'));
      continue;
    }
    let name = m[2].toLowerCase();
    name = ALIAS[name] ?? name;
    if (m[1]) {
      let at = stack.length - 1;
      while (at >= 0 && stack[at] !== name && stack[at] !== `!${name}`) at--;
      if (at < 0) continue;
      while (stack.length > at) close(stack.pop()!);
      continue;
    }
    if (!ALLOWED.has(name)) {
      if (!VOID.has(name) && !/\/\s*$/.test(m[3] ?? '') && !['img', 'hr', 'input', 'meta', 'link', 'wbr', 'source'].includes(name)) stack.push(`!${name}`);
      continue;
    }
    const attrs = parseAttrs(m[3] ?? '');
    let open = '';
    if (name === 'a') {
      const href = safeHref(attrs.get('href') ?? '');
      if (!href) {
        stack.push('!a');
        continue;
      }
      open = `<a href="${escapeAttr(href)}" rel="noopener noreferrer nofollow" target="_blank">`;
    } else if (name === 'span') {
      const id = attrs.get('data-mention') ?? attrs.get('data-id') ?? '';
      if (!MENTION_ID.test(id)) {
        stack.push('!span');
        continue;
      }
      const label = (attrs.get('data-label') ?? '').slice(0, 100);
      open = `<span data-mention="${escapeAttr(id)}"${label ? ` data-label="${escapeAttr(label)}"` : ''}>`;
    } else if (name === 'ol') {
      const start = Number(attrs.get('start'));
      open = Number.isInteger(start) && start > 1 && start < 100000 ? `<ol start="${start}">` : '<ol>';
    } else {
      open = `<${name}>`;
    }
    out.push(open);
    if (!VOID.has(name)) stack.push(name);
  }
  while (stack.length) close(stack.pop()!);
  return out.join('');
}

/** IDs aller erwähnten Personen (in der Reihenfolge des Auftretens, ohne Doppelte) */
export function commentMentionIds(html: string): string[] {
  const ids: string[] = [];
  const re = /<span\b[^>]*\bdata-mention="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) if (!ids.includes(m[1]!)) ids.push(m[1]!);
  return ids;
}

/** Lesbarer Text für Benachrichtigungen, Mails, Suche und Auszüge */
export function commentPlainText(body: string, format: number): string {
  if (format !== COMMENT_FORMAT_RICH) return body.trim();
  const text = body
    .replace(/<\/p>\s*<\/li>/gi, '</li>')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '• ')
    .replace(/<\/(p|li|blockquote|pre|ul|ol)>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return decodeEntities(text)
    .replace(/ /g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Kurzer Auszug in einer Zeile; ohne Text beschreibt er die Anhänge */
export function commentExcerpt(body: string, format: number, attachments: { isImage: boolean }[] = [], n = 140): string {
  const t = commentPlainText(body, format).replace(/\s+/g, ' ').trim();
  if (t) return t.length > n ? `${t.slice(0, n - 1)}…` : t;
  const imgs = attachments.filter((a) => a.isImage).length;
  const files = attachments.length - imgs;
  const parts = [imgs && (imgs === 1 ? '1 Bild' : `${imgs} Bilder`), files && (files === 1 ? '1 Datei' : `${files} Dateien`)].filter(Boolean);
  return parts.length ? `[${parts.join(', ')}]` : '';
}

/** Ein Kommentar ist leer, wenn weder Text noch Anhänge da sind */
export function isEmptyCommentBody(body: string, format: number): boolean {
  return !commentPlainText(body, format).replace(/\s+/g, '');
}
